import { Button as ShadcnButton } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    MoreHorizontal,
    RefreshCw,
    ShieldCheck,
    ShieldOff,
    Star,
    Unlink,
    UserPlus,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCustomerReadOnly } from '../../../CustomerReadOnlyContext'

interface ContactActionsMenuProps {
    hasKeycloakAccount: boolean
    hasNoAccount: boolean
    isAccountEnabled: boolean
    isContactPrimary: boolean
    onDisable: () => void
    isDisabling: boolean
    onEnable: () => void
    isEnabling: boolean
    onUnlink: () => void
    isUnlinking: boolean
    onSync: () => void
    isSyncing: boolean
    onSetPrimary: () => void
    isSettingPrimary: boolean
    onCreateAccount: () => void
}

const ContactActionsMenu: React.FC<ContactActionsMenuProps> = ({
    hasKeycloakAccount,
    hasNoAccount,
    isAccountEnabled,
    isContactPrimary,
    onDisable,
    isDisabling,
    onEnable,
    isEnabling,
    onUnlink,
    isUnlinking,
    onSync,
    isSyncing,
    onSetPrimary,
    isSettingPrimary,
    onCreateAccount,
}) => {
    const { t: tCustomer } = useTranslation('customer')
    const isReadOnly = useCustomerReadOnly()

    // Chế độ chỉ xem: mọi mục trong menu đều là thao tác ghi -> ẩn cả menu
    if (isReadOnly) return null

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <ShadcnButton
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 min-w-8"
                >
                    <MoreHorizontal className="h-4 w-4" />
                </ShadcnButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {hasKeycloakAccount && (
                    <>
                        {isAccountEnabled ? (
                            <DropdownMenuItem
                                onClick={onDisable}
                                disabled={isDisabling}
                                className="cursor-pointer text-red-600 focus:text-red-600"
                            >
                                <ShieldOff className="h-4 w-4 mr-2" />
                                {tCustomer('contact.disable-account')}
                            </DropdownMenuItem>
                        ) : (
                            <DropdownMenuItem
                                onClick={onEnable}
                                disabled={isEnabling}
                                className="cursor-pointer text-green-600 focus:text-green-600"
                            >
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                {tCustomer('contact.enable-account')}
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={onUnlink}
                            disabled={isUnlinking}
                            className="cursor-pointer text-orange-600 focus:text-orange-600"
                        >
                            <Unlink className="h-4 w-4 mr-2" />
                            {tCustomer('contact.unlink-account')}
                        </DropdownMenuItem>
                    </>
                )}
                {hasNoAccount && (
                    <DropdownMenuItem
                        onClick={onCreateAccount}
                        className="cursor-pointer text-blue-600 focus:text-blue-600"
                    >
                        <UserPlus className="h-4 w-4 mr-2" />
                        {tCustomer('contact.create-keycloak-account')}
                    </DropdownMenuItem>
                )}
                {(hasKeycloakAccount || hasNoAccount) && (
                    <DropdownMenuSeparator />
                )}
                <DropdownMenuItem
                    onClick={onSync}
                    disabled={isSyncing}
                    className="cursor-pointer"
                >
                    <RefreshCw
                        className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                    />
                    {tCustomer('contact.sync-account')}
                </DropdownMenuItem>
                {!isContactPrimary && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={onSetPrimary}
                            disabled={isSettingPrimary}
                            className="cursor-pointer text-yellow-600 focus:text-yellow-600"
                        >
                            <Star className="h-4 w-4 mr-2" />
                            {tCustomer('contact.set-as-primary-contact')}
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default ContactActionsMenu
